import { z } from 'zod'
import {
  AcceptInviteResponseSchema,
  CatalogCopyResponseSchema,
  CatalogItemSchema,
  CatalogResponseSchema,
  FrequentItemsResponseSchema,
  FxResponseSchema,
  GroupSchema,
  HistoryResponseSchema,
  InviteCreatedSchema,
  InvitePreviewSchema,
  InvitesResponseSchema,
  KauflandStoresResponseSchema,
  MeResponseSchema,
  OfferMatchesResponseSchema,
  OffersRefreshResponseSchema,
  OffersRefreshStatusSchema,
  OffersResponseSchema,
  OkResponseSchema,
  PushKeyResponseSchema,
  PushTestResponseSchema,
  StoreSchema,
  StoresResponseSchema,
  SyncResponseSchema,
  TemplateItemAddResponseSchema,
  TemplateSchema,
  TemplatesResponseSchema,
  TripSchema,
  TripWithItemsSchema,
  TripDealsResponseSchema,
  TripsResponseSchema,
  type CatalogCopyInput,
  type CatalogItemInputData,
  type CatalogItemPatchData,
  type LoginInput,
  type PushSubscriptionInput,
  type RegisterInput,
  type StoreInputData,
  type StorePatch,
  type SyncChange,
  type TemplateInputData,
  type TemplateItemAddData,
  type TemplatePatch,
  type TripInputData,
  type TripPatch,
} from '@sp/shared'
import { request } from './http.ts'

const ok = (d: unknown) => OkResponseSchema.parse(d)
const me = (d: unknown) => MeResponseSchema.parse(d)
const RegisterResponseSchema = MeResponseSchema.extend({ joinedGroupId: z.uuid().nullable() })

export const api = {
  auth: {
    me: () => request('GET', '/api/auth/me', me),
    login: (input: LoginInput) => request('POST', '/api/auth/login', me, input),
    register: (input: RegisterInput) =>
      request('POST', '/api/auth/register', (d) => RegisterResponseSchema.parse(d), input),
    logout: () => request('POST', '/api/auth/logout', ok),
    updateProfile: (name: string) => request('PATCH', '/api/auth/me', me, { name }),
    changePassword: (currentPassword: string, newPassword: string) =>
      request('POST', '/api/auth/password', ok, { currentPassword, newPassword }),
    resetPassword: (token: string, newPassword: string) =>
      request('POST', '/api/auth/reset-password', me, { token, newPassword }),
  },

  invites: {
    preview: (token: string) =>
      request('GET', `/api/invites/${encodeURIComponent(token)}`, (d) => InvitePreviewSchema.parse(d)),
    accept: (token: string) =>
      request('POST', `/api/invites/${encodeURIComponent(token)}/accept`, (d) => AcceptInviteResponseSchema.parse(d)),
    list: (groupId: string) =>
      request('GET', `/api/groups/${groupId}/invites`, (d) => InvitesResponseSchema.parse(d).invites),
    create: (groupId: string, label: string | null) =>
      request('POST', `/api/groups/${groupId}/invites`, (d) => InviteCreatedSchema.parse(d), { label }),
    revoke: (groupId: string, inviteId: string) =>
      request('DELETE', `/api/groups/${groupId}/invites/${inviteId}`, ok),
  },

  groups: {
    create: (name: string) => request('POST', '/api/groups', (d) => GroupSchema.parse(d), { name }),
    rename: (groupId: string, name: string) =>
      request('PATCH', `/api/groups/${groupId}`, (d) => GroupSchema.parse(d), { name }),
    removeMember: (groupId: string, userId: string) =>
      request('DELETE', `/api/groups/${groupId}/members/${userId}`, ok),
  },

  stores: {
    list: (groupId: string, archived = false) =>
      request(
        'GET',
        `/api/groups/${groupId}/stores${archived ? '?archived=1' : ''}`,
        (d) => StoresResponseSchema.parse(d).stores,
      ),
    create: (groupId: string, input: StoreInputData) =>
      request('POST', `/api/groups/${groupId}/stores`, (d) => StoreSchema.parse(d), input),
    update: (id: string, patch: StorePatch) =>
      request('PATCH', `/api/stores/${id}`, (d) => StoreSchema.parse(d), patch),
    archive: (id: string) => request('DELETE', `/api/stores/${id}`, ok),
    reorder: (groupId: string, storeIds: string[]) =>
      request('PUT', `/api/groups/${groupId}/stores/order`, (d) => StoresResponseSchema.parse(d).stores, { storeIds }),
  },

  catalog: {
    list: (groupId: string) =>
      request('GET', `/api/groups/${groupId}/catalog`, (d) => CatalogResponseSchema.parse(d).items),
    create: (groupId: string, input: CatalogItemInputData) =>
      request('POST', `/api/groups/${groupId}/catalog`, (d) => CatalogItemSchema.parse(d), input),
    update: (id: string, patch: CatalogItemPatchData) =>
      request('PATCH', `/api/catalog/${id}`, (d) => CatalogItemSchema.parse(d), patch),
    addBarcode: (id: string, code: string) =>
      request('POST', `/api/catalog/${id}/barcodes`, (d) => CatalogItemSchema.parse(d), { code }),
    copy: (targetGroupId: string, input: CatalogCopyInput) =>
      request('POST', `/api/groups/${targetGroupId}/catalog/copy`, (d) => CatalogCopyResponseSchema.parse(d), input),
    archive: (id: string) => request('DELETE', `/api/catalog/${id}`, ok),
  },

  templates: {
    list: (groupId: string) =>
      request('GET', `/api/groups/${groupId}/templates`, (d) => TemplatesResponseSchema.parse(d).templates),
    create: (groupId: string, input: TemplateInputData) =>
      request('POST', `/api/groups/${groupId}/templates`, (d) => TemplateSchema.parse(d), input),
    update: (id: string, patch: TemplatePatch) =>
      request('PATCH', `/api/templates/${id}`, (d) => TemplateSchema.parse(d), patch),
    archive: (id: string) => request('DELETE', `/api/templates/${id}`, ok),
    /** Zbozi podle nazvu — zname ve skupine se pouzije, jinak se zalozi. */
    addItem: (id: string, input: TemplateItemAddData) =>
      request('POST', `/api/templates/${id}/items`, (d) => TemplateItemAddResponseSchema.parse(d), input),
    setItemQty: (id: string, catalogItemId: string, qty: number | null) =>
      request('PUT', `/api/templates/${id}/items/${catalogItemId}`, (d) => TemplateSchema.parse(d), { qty }),
    removeItem: (id: string, catalogItemId: string) =>
      request('DELETE', `/api/templates/${id}/items/${catalogItemId}`, (d) => TemplateSchema.parse(d)),
  },

  trips: {
    list: (groupId: string) =>
      request('GET', `/api/groups/${groupId}/trips`, (d) => TripsResponseSchema.parse(d).trips),
    create: (groupId: string, input: TripInputData) =>
      request('POST', `/api/groups/${groupId}/trips`, (d) => TripWithItemsSchema.parse(d), input),
    get: (id: string) => request('GET', `/api/trips/${id}`, (d) => TripWithItemsSchema.parse(d)),
    update: (id: string, patch: TripPatch) =>
      request('PATCH', `/api/trips/${id}`, (d) => TripSchema.parse(d), patch),
    remove: (id: string) => request('DELETE', `/api/trips/${id}`, ok),
    sync: (id: string, changes: SyncChange[]) =>
      request('POST', `/api/trips/${id}/sync`, (d) => SyncResponseSchema.parse(d), { changes }),
    /** Akce pro nekoupene polozky v obchodech trasy v den nakupu. */
    deals: (id: string, itemId?: string | null) =>
      request('GET', `/api/trips/${id}/deals${itemId ? `?item=${itemId}` : ''}`, (d) => TripDealsResponseSchema.parse(d)),
    frequent: (groupId: string) =>
      request('GET', `/api/groups/${groupId}/frequent`, (d) => FrequentItemsResponseSchema.parse(d).names),
  },

  offers: {
    list: (
      groupId: string,
      params: {
        q?: string
        chain?: string
        /** Jen tyto retezce (obchody nakupu v dany den); prazdne pole = zadne akce. */
        chains?: readonly string[]
        date?: string
        category?: string
        limit?: number
        offset?: number
      } = {},
    ) => {
      const search = new URLSearchParams()
      if (params.q) search.set('q', params.q)
      if (params.category) search.set('category', params.category)
      if (params.chain) search.set('chain', params.chain)
      if (params.chains) search.set('chains', params.chains.join(','))
      if (params.date) search.set('date', params.date)
      if (params.limit) search.set('limit', String(params.limit))
      if (params.offset) search.set('offset', String(params.offset))
      const query = search.toString()
      return request('GET', `/api/groups/${groupId}/offers${query ? `?${query}` : ''}`, (d) => OffersResponseSchema.parse(d))
    },
    /** Prodejny Kauflandu v Cesku nebo Nemecku pro vyber u obchodu. */
    kauflandStores: (country: 'cz' | 'de') =>
      request('GET', `/api/offers/kaufland-stores?country=${country}`, (d) => KauflandStoresResponseSchema.parse(d).stores),
    /**
     * date = den nakupu („2026-09-19"): jen akce platne v ten den. source = watched |
     * template:<id> | trip:<id>; bez nej vse zbozi skupiny (vyber a planovani).
     */
    matches: (groupId: string, date?: string | null, source?: string | null) => {
      const search = new URLSearchParams()
      if (date) search.set('date', date)
      if (source) search.set('source', source)
      const query = search.toString()
      return request(
        'GET',
        `/api/groups/${groupId}/offers/matches${query ? `?${query}` : ''}`,
        (d) => OfferMatchesResponseSchema.parse(d).matches,
      )
    },
    /** Hned zkusit nove letaky obchodu skupiny; sber bezi na pozadi. */
    refresh: (groupId: string) =>
      request('POST', `/api/groups/${groupId}/offers/refresh`, (d) => OffersRefreshResponseSchema.parse(d), {}),
    refreshStatus: (groupId: string) =>
      request('GET', `/api/groups/${groupId}/offers/refresh`, (d) => OffersRefreshStatusSchema.parse(d)),
  },

  push: {
    key: () => request('GET', '/api/push/key', (d) => PushKeyResponseSchema.parse(d)),
    subscribe: (input: PushSubscriptionInput) => request('POST', '/api/push/subscribe', ok, input),
    unsubscribe: (endpoint: string) => request('POST', '/api/push/unsubscribe', ok, { endpoint }),
    test: () => request('POST', '/api/push/test', (d) => PushTestResponseSchema.parse(d)),
  },

  history: (groupId: string) =>
    request('GET', `/api/groups/${groupId}/history`, (d) => HistoryResponseSchema.parse(d).items),

  fx: () => request('GET', '/api/fx', (d) => FxResponseSchema.parse(d)),
}
